import { useState, memo } from 'react';
import { RacePhaseCarousel } from '@/components/RacePhaseCarousel';
import { CountdownBanner } from '@/components/CountdownBanner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ChevronDown, ChevronRight, Copy, MoreVertical } from 'lucide-react';

interface Phase {
  id: string;
  title: string;
  isPlaceholder?: boolean;
}

interface ChecklistItem {
  id: string;
  item: string;
  comment: string;
}

interface SharedSectionProps {
  id: string;
  name: string;
  phases: Phase[];
  phaseTitles: Record<string, string>;
  onPhaseSelect: (phaseId: string, phaseTitle: string) => void;
  targetDate?: Date | null;
  countdownEnabled?: boolean;
  externalCollapsed?: boolean;
  onExternalToggleCollapse?: () => void;
  onCopySection?: (sectionId: string) => void;
  showWatermark?: boolean;
  phaseItems?: Record<string, ChecklistItem[]>;
}

const SharedSectionComponent = ({
  id,
  name,
  phases,
  phaseTitles,
  onPhaseSelect,
  targetDate,
  countdownEnabled = false,
  externalCollapsed,
  onExternalToggleCollapse,
  onCopySection,
  showWatermark = true,
  phaseItems
}: SharedSectionProps) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  
  // Use external collapsed state if provided, otherwise use internal state
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;
  const toggleCollapsed = onExternalToggleCollapse || (() => setInternalCollapsed(!internalCollapsed));

  const actualPhases = phases.filter(phase => !phase.isPlaceholder);

  return (
    <TooltipProvider>
      <Collapsible open={!isCollapsed} onOpenChange={(open) => toggleCollapsed()}>
        <Card
        className={`mb-8 border border-border bg-card relative z-20 transition-colors duration-200 hover:border-destructive pt-1`}
        style={{ 
          minHeight: isCollapsed ? 'auto' : '320px'
        }}
      >
        <CardHeader className="px-10 py-2 lg:py-4 space-y-0">
          {/* Desktop layout */}
          <div className="hidden lg:flex items-center w-full gap-2">
            {/* Zone 1: Collapse Arrow - anchored left */}
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-11 w-11 p-0 hover:bg-muted min-h-[44px] min-w-[44px] flex-shrink-0 -ml-6"
                aria-label={isCollapsed ? `Expand ${name} section` : `Collapse ${name} section`}
              >
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>

            {/* Zone 2: Name - fills available space */}
            <h2 
              className="text-xl font-normal text-foreground truncate flex-1 min-w-0"
              title={name}
              aria-label={name}
            >
              {name}
            </h2>
            
            {/* Zone 3: Copy Button - anchored right */}
            {onCopySection && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={() => onCopySection(id)} 
                    variant="ghost" 
                    size="sm" 
                    className="h-11 w-11 p-0 flex-shrink-0 -mr-6"
                    aria-label="Copy to My Event Checklists"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="z-[99999]">Copy to My Event Checklists</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Mobile/Tablet layout */}
          <div className="flex flex-col lg:hidden">
            <div className="flex items-center w-full gap-2">
              {/* Zone 1: Collapse Arrow */}
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0 -ml-6"
                  aria-label={isCollapsed ? "Expand event" : "Collapse event"}
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>

              {/* Zone 2: Name - fills available space */}
              <h2 
                className="text-xl font-normal text-foreground truncate flex-1 min-w-0 h-8 flex items-center"
                title={name}
                aria-label={name}
              >
                {name}
              </h2>
              
              {/* Zone 3: 3-dot Menu - anchored right */}
              {onCopySection && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0 -mr-6">
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">More actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-card border border-border z-[99999]">
                    <DropdownMenuItem onClick={() => onCopySection(id)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy to My Event Checklists
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className={`pt-0 ${showWatermark ? 'sample-watermark-container relative' : ''}`} style={showWatermark ? { minHeight: '300px' } : undefined}>
            {showWatermark && (
              <div className="sample-watermark" aria-hidden="true">SAMPLE</div>
            )}
            {/* Countdown Banner */}
            {countdownEnabled && targetDate && (
              <CountdownBanner
                targetDate={targetDate}
                countdownEnabled={countdownEnabled}
                eventName={name}
                isLoggedIn={false}
                readOnly={true}
              />
            )}

            {/* Race Phase Carousel */}
            <RacePhaseCarousel
              phases={actualPhases}
              onPhaseSelect={onPhaseSelect}
              phaseTitles={phaseTitles}
              isStaticReference={true}
              showLoginPrompt={false}
              phaseItems={phaseItems}
            />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
    </TooltipProvider>
  );
};

export const SharedSection = memo(SharedSectionComponent);